import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the browser set multipart boundary for GridFS uploads.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  register: (data) => api.post('/auth/register', data),
  checkEmail: (params) => api.get('/auth/check-email', { params }),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/profile'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  validateResetToken: (token) => api.get('/auth/reset-password/validate', { params: { token } }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const configService = {
  getOrientationWindow: () => api.get('/config/orientation-window'),
};

export const orientationSubmissionService = {
  submit: (data) => api.post('/employee-orientations', data),
  submitGuest: (data) => api.post('/employee-orientations/guest', data),
  listByRegistration: (registrationId) => api.get(`/employee-orientations/registration/${registrationId}`),
  getLatest: (registrationId) => api.get(`/employee-orientations/registration/${registrationId}/latest`),
  /** Calendar OR admin override for one employee. */
  getWindow: (registrationId) => api.get(`/employee-orientations/registration/${registrationId}/window`, {
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
    params: { _ts: Date.now() },
  }),
};

export const employeeRegistrationService = {
  create: (data) => api.post('/employee-registrations', data),
  list: (params = {}) => api.get('/employee-registrations', {
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      'If-None-Match': '',
      'If-Modified-Since': '',
    },
    params: { ...params, _ts: Date.now() },
  }),
  getById: (id) => api.get(`/employee-registrations/${id}`, {
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      'If-None-Match': '',
      'If-Modified-Since': '',
    },
    params: { _ts: Date.now() },
  }),
  update: (id, data) => api.put(`/employee-registrations/${id}`, data),
  remove: (id) => api.delete(`/employee-registrations/${id}`),
  sendAdminDocument: (id, payload) => api.post(`/employee-registrations/${id}/send-admin-document`, payload),
  updateStatus: (id, payload) => api.patch(`/employee-registrations/${id}/status`, payload),
  setOrientationWindowOverride: (id, enabled) =>
    api.patch(`/employee-registrations/${id}/orientation-window-override`, { enabled }),
  /** New uploads → GridFS. Returns { gridFsId, fileName, fileType, fileSize, storage }. */
  uploadDocument: (file, docKey = '') => {
    const form = new FormData();
    form.append('file', file);
    if (docKey) form.append('docKey', docKey);
    return api.post('/employee-registrations/documents/upload', form, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  /** Dual-mode download: GridFS or legacy base64. */
  downloadDocument: (id, docKey) => api.get(
    `/employee-registrations/${id}/documents/${encodeURIComponent(docKey)}`,
    { responseType: 'blob' }
  ),
};

export const subadminService = {
  list: () => api.get('/subadmins'),
  update: (id, data) => api.put(`/subadmins/${id}`, data),
  remove: (id) => api.delete(`/subadmins/${id}`),
};

export default api;
